import {nextTick} from 'vue';
import VueMultiselect from 'vue-multiselect';
import anime from 'animejs';
import axios from 'axios';
import vuescroll from '@centaurea/vue3-scroll';
import {Carousel, Slide} from '@centaurea/vue3-carousel';
import Vue3TouchEvents from 'vue3-touch-events';
import VueLazyload from 'vue-lazyload';
import invokeSmartScripts from '../shared/smart-script';
import {globalActions} from '../store/global-actions';
import {store} from '../store/global-store';
import {Cookies} from '../shared/cookies-extended';
import {SimpleBar} from 'simplebar-vue3'; // ; TODO: Fix when resolve proper plugin
import 'simplebar/dist/simplebar.min.css';
import VueTimepicker from 'vue3-timepicker';
import 'vue3-timepicker/dist/VueTimepicker.css';
import {validate} from '../shared/validation';
import moment from 'moment';
import PopUpShowTimer from '../shared/pop-up-show-timer';
import _u from '../shared/utils';
import localizationService from '../shared/services/localization-service';
import VueDatepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import {updateModelValueEmit} from './inputs/currency-price';
import { emitEvents } from '../shared/emit-events';

const Dropdown = {
    props: {
        withOverlay: {},
        event: {},
    },
    emits: ['open', 'close'],
    data() {
        return {
            isOpen: false,
            uniqueId: _u.genUid(),
        };
    },
    methods: {
        open() {
            this.isOpen = true;
            this.$emit('open');
            globalActions.openDropdown(this.uniqueId);
        },

        close() {
            this.isOpen = false;
            this.$emit('close');
            globalActions.closeDropdown(this.uniqueId);
        },
        toggle() {
            this.isOpen ? this.close() : this.open();
        },
    },
};

const collapseContent = {
    extends: Dropdown,
    template: '#collapse-content',
    props: {
        hideButtonAfterOpen: {
            type: Boolean,
        },
        onClickOutsideAction: {
            type: Function,
        },
        hasTransition: {
            type: Boolean,
            default: true,
        },
    },
    methods: {
        onClickOutside() {
            if (this.onClickOutsideAction) {
                this.onClickOutsideAction();
            }
            this.close();
        },
    },
};

const longContentExpander = {
    extends: Dropdown,
    template: '#long-content-expander',
    props: {
        contentId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            contentHeight: Number.MAX_SAFE_INTEGER,
            containerHeight: 0,
        };
    },
    computed: {
        isCollapsible() {
            return this.contentHeight > this.containerHeight;
        },
    },
    mounted() {
        this.contentHeight = this.$el.querySelector(`#${this.contentId}`).scrollHeight;
        this.containerHeight = this.$refs.contentContainer.getBoundingClientRect().height;
    },
};

const dropdownDynamic = {
    extends: Dropdown,
    template: '#dropdown-dynamic',
    props: {
        options: {
            type: Array,
            required: true,
        },
        modelValue: {},
        labelField: {
            type: String,
            required: false,
            default: 'label',
        },
        valueField: {
            type: String,
            required: false,
            default: 'value',
        },
        initialValue: {
            required: false,
        },
    },
    data() {
        return {
            current: this.modelValue || (this.initialValue ? this.findOption(this.initialValue) : this.options[0]),
        };
    },
    emits: [updateModelValueEmit],
    watch: {
        modelValue(val) {
            this.current = val;
        },

        current(val) {
            this.$emit(updateModelValueEmit, val);
        },
    },
    computed: {
        selectedValue() {
            return this.current[this.valueField];
        },
    },
    methods: {
        findOption(value) {
            return this.options.find((o) => o[this.valueField] === value);
        },
        getLabel(option) {
            return option ? option[this.labelField] : null;
        },
        select(option) {
            this.current = option;
            this.close();
        },

        isSelected(option) {
            if (!this.current || !option) {
                return false;
            }

            if (this.current === option) {
                return true;
            }

            return this.current[this.valueField] === option[this.valueField];
        },
    },
    mounted() {
        if (!this.current) {
            this.select();
        }
    },
};

const stretchedImg = {
    template: '#stretched-img',
    props: {
        watchElement: {},
    },
    data() {
        return {
            duration: 125,
        };
    },
    methods: {
        _refreshSize(mutationList) {
            if (this.$el.style.display !== 'none' &&
                this.$el.style.opacity !== '0' &&
                this.$el.style.visibility !== 'hidden') {
                const mutationTarget = mutationList ? mutationList.find((r) => r.target !== this.$el) : document;
                if (mutationTarget) {
                    const watchHeight = this._getWatchElementHeight();
                    const elRect = this.$el.getBoundingClientRect();
                    const currentHeight = elRect.height;
                    const currentTop = elRect.y;

                    if (currentHeight !== watchHeight) {
                        const animate = (fromY, toY, scaleFrom, scaleTo) => {
                            if (!this._animationInProgress && (fromY !== toY || scaleFrom !== scaleTo)) {
                                this._animationInProgress = true;
                                anime({
                                    targets: this.$el,
                                    scaleY: {
                                        value: scaleTo,
                                        duration: this.duration,
                                    },
                                    translateY: {
                                        value: toY,
                                        duration: this.duration,
                                    },
                                    easing: 'easeInOutCubic',
                                }).finished.then(() => {
                                    this._animationInProgress = false;
                                });
                            }
                        };

                        if (watchHeight > this._originalHeight) {
                            // scale up and move to the very top
                            const scaleTo = watchHeight / parseFloat(this._originalHeight);
                            animate(currentTop, 0, 1, scaleTo);
                        } else if (currentHeight !== this._originalHeight) {
                            // scale down and move
                            const scaleFrom = currentHeight / parseFloat(watchHeight);
                            animate(currentTop, watchHeight - this._originalHeight, scaleFrom, 1);
                        } else {
                            // just move
                            animate(currentTop, watchHeight - currentHeight, 1, 1);
                        }
                    }
                }
            }
        },

        _bindChangesListeners() {
            const observer = new MutationObserver(this._refreshSize);
            observer.observe(document.getElementById(this.watchElement),
                {attributes: true, childList: true, subtree: true});
            window.addEventListener('resize',
                () => {
                    this._setInitialHeight();
                    this._refreshSize();
                });
        },

        _getWatchElementHeight() {
            return this._watchEl.getBoundingClientRect().height;
        },

        _setInitialHeight() {
            let height = this.$el.getBoundingClientRect().height;
            if (height === 0) {
                height = window.innerHeight;
                this.$el.style.height = `${height}px`;
            }

            this._originalHeight = height;
        },
    },
    mounted() {
        this._watchEl = document.getElementById(this.watchElement);
        this._setInitialHeight();
        this._refreshSize();
        this._bindChangesListeners();
    },
};

const moreHeaderActions = {
    template: '#more-header-actions',
    props: {
        hideMessages: {},
    },
    computed: {
        hasMoreAuthorizedActions() {
            return this.$slots['more-authorized-actions'] != null || this.hasMoreActions;
        },

        hasMoreNotAuthorizedActions() {
            return this.$slots['more-not-authorized-actions'] != null || this.hasMoreActions;
        },

        hasMoreActions() {
            return this.$slots['more-actions'] != null;
        },

        unreadMessagesCount() {
            return store.state.shared.unreadMessagesCount;
        },
    },
    methods: {
        openChatList() {
            store.commit('openChat');
        },
        closeMenu() {
            this.$refs.menu.close();
        },
    },
};

const animatedLogo = {
    template: '#animated-logo',
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
        showLabel: {
            type: Boolean,
            default: false,
        },
        label: {},
        isFlexible: {
            type: Boolean,
            default: false,
        },
    },
};

const httpMethods = Object.freeze({
    get: 'get',
    post: 'post',
});

const asyncComponent = {
    template: '#async-component',
    props: {
        src: String,
        asyncData: Object,
        progressTimeout: Number,
        cleanPrev: {
            type: Boolean,
            default: true,
        },
        requestBody: {
            type: Object,
            required: false,
        },
        httpMethod: {
            type: String,
            default: httpMethods.get,
        },
        allowAnimation: {
            type: Boolean,
            default: true,
        },
        slotClass: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            loading: false,
            html: null,
            error: null,
        };
    },
    computed: {
        srcInner() {
            return this.src;
        },
        component() {
            const that = this;
            return {
                template: this.html ? this.html : '<div></div>',
                data() {
                    return Object.assign({}, that.asyncData);
                },
            };
        },
        loaded() {
            return !this.loading && this.html && !this.error;
        },
        showAnimation() {
            return this.allowAnimation && this.loading;
        },
    },
    watch: {
        srcInner: {
            handler(src) {
                if (src) {
                    this._loadData(src);
                } else {
                    this.html = null;
                }
            },
            immediate: true,
        },
    },
    methods: {
        reload() {
            this._loadData(this.srcInner);
        },

        openFeedback() {
            globalActions.openFeedback();
        },

        _initLoad() {
            this._reset();
            this._setLoading();
        },

        _reset(force) {
            if (this.cleanPrev || force) {
                this.html = null;
            }
            this.error = null;
        },

        _setLoading() {
            if (this.progressTimeout) {
                setTimeout(() => {
                    if (!this.loaded) {
                        this._reset(true);
                        this.loading = true;
                    }
                },
                this.progressTimeout);
            } else {
                this.loading = true;
            }
        },

        _loadData(src) {
            this._initLoad();
            if (this.httpMethod === httpMethods.get) {
                axios.get(src)
                    .then(this._loaded)
                    .catch(this._error);
            } else {
                axios.post(src, this.requestBody)
                    .then(this._loaded)
                    .catch(this._error);
            }
        },

        _loaded(res) {
            this.loading = false;
            this.html = res.data;
            if (this.$refs.htmlContainer) {
                invokeSmartScripts(this.$refs.htmlContainer.$el);
            }
        },

        _error(error) {
            this.loading = false;
            this.error = error.message;
        },
    },
};

const mediaCarousel = {
    template: '#media-carousel',
    props: {
        photos: {},
        video: {},
        allowFullscreen: {},
        defaultAlt: {},
    },
    data() {
        return {
            current: 0,
            currentLarge: 0,
            isFullScreen: false,
            isBrowserFullScreen: false,
            transitionSpeed: 250,
            fullScreenTransitionSpeed: 0,
            mediaToLoadAmount: 3,
            initialized: false,
            fullscreenInitialized: false,
            videoFrameCanBeLoaded: true,
        };
    },
    computed: {
        hasNext() {
            return this._getCurrentPage() < this.media.length - 1;
        },

        hasPrev() {
            return this._getCurrentPage() > 0;
        },

        media() {
            let res = [];

            if (this.hasAvailableVideo) {
                res = res.concat(this.video);
            }

            if (this.photos && this.photos.length) {
                res = res.concat(this.photos);
            }

            return res;
        },

        hasAvailableVideo() {
            return this.video && this.videoFrameCanBeLoaded;
        },
    },
    watch: {
        isFullScreen(val) {
            nextTick(() => {
                this.fullscreenInitialized = val;
            });
            if (!val && this.isBrowserFullScreen) {
                this._disableBrowserFullScreen();
            }
        },
    },
    mounted() {
        this.probIsBlockedAsync();

        this.$nextTick(() => {
            this.initialized = true;
        });
    },
    methods: {
        videoLoaded() {
            this.videoFrameCanBeLoaded = true;
        },

        async probIsBlockedAsync() {
            try {
                await fetch(this.video, { mode: "no-cors" });
            } catch {
                this.videoFrameCanBeLoaded = false;
            }
        },

        syncCurrent(i) {
            this.current = i;
        },

        syncCurrentLarge(i) {
            if (!this.fullScreenTransitionSpeed) {
                nextTick(() => {
                    this.fullScreenTransitionSpeed = this.transitionSpeed;
                });
            }
            this.currentLarge = i;
        },

        prev() {
            this._changePage((x) => x - 1);
        },

        next() {
            this._changePage((x) => x + 1);
        },

        showFullscreen() {
            if (this.allowFullscreen) {
                this.currentLarge = this.current;
                this.isFullScreen = true;
            }
        },

        fullscreenClosed() {
            this.fullScreenTransitionSpeed = 0;
        },

        switchBrowserFullScreen() {
            this.isBrowserFullScreen = !this.isBrowserFullScreen;
            if (this.isBrowserFullScreen) {
                this._enableBrowserFullScreen();
            } else {
                this._disableBrowserFullScreen();
            }
        },

        getMediaSource(media, index, isFullScreen = false) {
            const mediaIndex = this.hasAvailableVideo ? index + 1 : index;
            const currentIndex = isFullScreen ? this.currentLarge : this.current;

            const shouldLoad = mediaIndex - currentIndex < this.mediaToLoadAmount;
            return shouldLoad ? media.hRef : media.sampleHRef;
        },

        _changePage(f) {
            if (this.isFullScreen) {
                this.currentLarge = f(this.currentLarge);
            } else {
                this.current = f(this.current);
            }

            if (this.hasAvailableVideo) {
                const frame = this.$refs.videoFrame;
                frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            }
        },

        _getCurrentPage() {
            return this.isFullScreen ? this.currentLarge : this.current;
        },

        _enableBrowserFullScreen() {
            if (!document.fullscreenElement) {
                this.$refs.fullscreenModal.$el.requestFullscreen();
                // subscribe to exit from fullscreen mode (maybe initiated with 'esc' for example)
                this.$refs.fullscreenModal.$el.onfullscreenchange = (event) => {
                    const elem = event.target;
                    this.isBrowserFullScreen = document.fullscreenElement === elem;
                };
            }
        },

        _disableBrowserFullScreen() {
            document.exitFullscreen();
        },
    },
};

const scrollTracker = {
    template: '#scroll-tracker',
    props: {
        offset: {type: Number},
        offsetEl: {type: String},
        scrolledOutClass: {type: String},
        scrolledInClass: {type: String},
        active: {type: Boolean, default: true},
    },
    data() {
        return {
            isScrolledOut: false,
        };
    },
    methods: {
        onScroll(_e, position) {
            if (!this.active) {
                return;
            }

            if (!this.isScrolledOut && this._isScrolledOut(position)) {
                this.isScrolledOut = true;
                this.$emit('scrolled-out');
            }

            if (this.isScrolledOut && !this._isScrolledOut(position)) {
                this.isScrolledOut = false;
                this.$emit('scrolled-in');
            }
        },

        hasOffset() {
            return this.offset || this.offsetEl;
        },

        _isScrolledOut(position) {
            return this._isOffsetExceeded(position) || this._isElementOut();
        },

        _isOffsetExceeded(position) {
            return !this.offsetEl &&
                this.offset &&
                (position.scrollTop > this.offset || position.scrollLeft > this.offset);
        },

        _isElementOut() {
            let isOut = false;

            if (this.offsetEl) {
                const elem = document.getElementById(this.offsetEl);

                if (elem) {
                    const rect = elem.getBoundingClientRect();
                    const offset = this.offset ? this.offset : 0;
                    isOut = rect.top + rect.height + offset < 0;
                }
            }

            return isOut;
        },
    },
};

const ACCEPTED = 'clicked';
const Banner = {
    props: {
        cookie: {},
        hasClose: {},
    },
    data() {
        return {
            cookieExpiration: 365 * 1000,
            clicked: false,
            active: false,
            timeoutSec: 0,
            acceptedCookie: null,
        };
    },
    computed: {
        accepted() {
            return this.clicked || this.acceptedCookie;
        },
        opened() {
            return this.active;
        },
        visible() {
            return !this.accepted && this.active;
        },
    },
    methods: {
        async accept() {
            await Cookies.setWithBackupAsync(this.cookie, ACCEPTED, {expires: this.cookieExpiration});
            this.clicked = true;
            this.active = false;
            await this._refreshCookiesStateAsync();
        },

        close() {
            this.accept();
        },

        open() {
            this.active = true;
        },

        initBanner() {
            new PopUpShowTimer({name: this.cookie, delayTime: this.timeoutSec * 1000})
                .start()
                .then(() => this.open());
        },

        async _refreshCookiesStateAsync() {
            this.acceptedCookie = await Cookies.getWithBackupCheckAsync(this.cookie) === ACCEPTED;
        },
    },
    async mounted() {
        await this._refreshCookiesStateAsync();
        if (!this.accepted) {
            this.initBanner();
        }
    },
};

const advertSearch = {
    template: '#advert-search',
    components: {
        Multiselect: VueMultiselect,
    },
    props: {
        placeholder: String,
        mapSettings: Object,
        placeDetailsUrl: String,
        placeDetailsByAddressUrl: String,
        propertyOffer: String,
        searchMode: {
            type: String,
            default: null,
        },
        showSearchButton: Boolean,
        switchFullscreenModeLayout: {
            type: Boolean,
            default: true,
        },
        isSolidarity: Boolean,
        formName: String,
        searchModeOptions: Array,
    },
    data() {
        return {
            address: null,
            searchLat: null,
            searchLon: null,
            searchBounds: null,
            searchEntry: null,
            propertyType: null,
            isActive: false,
            isFullMode: false,
            isSearchModeVisible: this.searchMode === null,
            uniqueId: _u.genUid(),
            searchModes: this.searchModeOptions,
        };
    },
    watch: {
        searchModes(val) {
            if (val) {
                this.searchEntry = val.find((i) => i.key === this.searchMode);
            }
        },
    },
    computed: {
        searchUrl() {
            return this.searchEntry ? this.searchEntry.value : null;
        },

        propertyTypes() {
            return this.propertyType ? [this.propertyType.value] : null;
        },
    },
    methods: {
        setActive(active) {
            this.isActive = active;
            this._focusInput();
        },

        switchMode() {
            this.isFullMode = !this.isFullMode;
            this._focusInput();
        },

        switchToCompact() {
            if (this.isFullMode) {
                this.switchMode();
            }
        },

        switchToFull() {
            if (!this.isFullMode) {
                this.switchMode();
            }
        },

        getSearchModes(searchUrls, offerType) {
            this.searchModes = searchUrls[offerType];
            return searchUrls[offerType];
        },

        _focusInput() {
            if (this.isFullMode && !this.isActive) {
                this.$refs.autocomplete.focusInput();
            }
        },

        _initAutocomplete() {
            store.subscribe((mutation) => {
                if (mutation.type === 'autocompleteCallback') {
                    this._autocompleteCallback(mutation.payload);
                }
            });
        },

        _autocompleteCallback(result) {
            if (!result) {
                console.error(`Cannot find entered address`);
                validate(this.$refs.searchForm, '[name="SearchLat"]');
                return;
            }

            this.searchLat = result.latitude;
            this.searchLon = result.longitude;
            this.searchBounds = JSON.stringify(result.bounds);
            this.address = result.formatted;

            nextTick(() => this.$refs.searchForm.submit());
        },
    },
    mounted() {
        this._initAutocomplete();
    },
};

const datepicker = {
    template: '#datepicker',
    props: {
        modelValue: {},
        language: {type: String},
    },
    components: {
        VueDatepicker: VueDatepicker,
    },
    emits: [updateModelValueEmit],
    data() {
        const firstAvailableDate = new Date();
        firstAvailableDate.setDate(firstAvailableDate.getDate() - 1);
        return {
            date: this.modelValue,
            disabledDates: firstAvailableDate,
            isOpened: false,
            languageCode: localizationService.getLanguageCode(this.language),
            dateFormat: localizationService.getDateFormatByLanguageCode(this.language),
            formatLocale: localizationService.getFormatLocale(this.language),
        };
    },
    watch: {
        date(value) {
            this.$emit(updateModelValueEmit, moment(value).toISOString());
        },
    },
};

const timepicker = {
    template: '#timepicker',
    props: {
        modelValue: {},
        minuteInterval: {},
    },
    components: {VueTimepicker},
    data() {
        return {
            time: this.getTimeModel(this.modelValue),
            refresh: null,
        };
    },
    emits: [updateModelValueEmit],
    methods: {
        getTimeModel(dateString) {
            const date = new Date(dateString);
            const hours = date.getHours();
            const halfOfDayInHours = 12;
            const hours12Format = hours % halfOfDayInHours ? hours % halfOfDayInHours : halfOfDayInHours;

            return {
                HH: this.formatTimeValue(hours),
                hh: this.formatTimeValue(hours12Format),
                mm: this.formatTimeValue(date.getMinutes()),
                A: hours >= halfOfDayInHours ? 'PM' : 'AM',
            };
        },
        getDateTimeString(timeModel) {
            const date = new Date(this.modelValue);
            date.setHours(Number(timeModel.HH));
            date.setMinutes(Number(timeModel.mm));
            return moment(date).toISOString();
        },
        formatTimeValue(currentValue) {
            return (`0${currentValue}`).slice(-2);
        },
        onInput(time) {
            this.$emit(updateModelValueEmit, this.getDateTimeString(time));
        },
        scrollToSelectedTime() {
            this.$nextTick(() => {
                this.scrollToSelected('.hours');
                this.scrollToSelected('.minutes');
            });
        },
        scrollToSelected(containerSelector) {
            const el = this.$el.querySelector(`${containerSelector} .active`);

            el.scrollIntoView(true);
        },
    },
    watch: {
        'modelValue': {
            handler(value) {
                this.time = this.getTimeModel(value);
                this.refresh = this.time;
            },
            immediate: true,
        },
    },
};

const orderPlan = {
    template: '#order-plan',
    props: {
        type: {
            type: String,
            required: true,
        },
        periodText: {
            type: String,
            required: false,
        },
        price: {
            type: Number,
            required: false,
            default: 0,
        },
        planName: {
            type: String,
            required: true,
        },
        href: {
            type: String,
            required: true,
        },
        idActionButton: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            isCompactMode: false,
            isDetailsVisible: false,
        };
    },
    methods: {
        showPlanDetails() {
            return !this.isCompactMode || this.isDetailsVisible;
        },
        seePlanDetails() {
            this.isDetailsVisible = !this.isDetailsVisible;
        },
        isMobile() {
            return window.innerWidth < 992;
        },
        _setCompactMode() {
            this.isCompactMode = this.isMobile();
        },
    },
    mounted() {
        this._setCompactMode();
        window.addEventListener('resize', this._setCompactMode, false);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this._setCompactMode);
    },
};

function initCustomControls(app) {
    app.use(vuescroll);
    app.component('carousel', Carousel);
    app.component('slide', Slide);
    app.use(Vue3TouchEvents);
    app.use(VueLazyload, {lazyComponent: true});
    app.component('simplebar', SimpleBar);

    app.component('dropdown', {
        extends: Dropdown,
        template: '#dropdown',
    });

    app.component('dropdown-static', {
        extends: Dropdown,
        template: '#dropdown-static',
        props: {
            classOnOpen: {
                type: String,
                required: false,
            },
            headerClass: {
                type: String,
                required: false,
                default: '',
            },
        },
    });

    app.component('collapse-content', collapseContent);
    app.component('long-content-expander', longContentExpander);
    app.component('dropdown-dynamic', dropdownDynamic);
    app.component('stretched-img', stretchedImg);

    app.component('authorize-button', {
        template: '#authorize-button',
        methods: {
            openModal() {
                globalActions.openLoginModal();
            },
        },
    });

    app.component('more-header-actions', moreHeaderActions);
    app.component('animated-logo', animatedLogo);
    app.component('async-component', asyncComponent);
    app.component('media-carousel', mediaCarousel);
    app.component('scroll-tracker', scrollTracker);

    app.component('cookies-banner', {
        extends: Banner,
        template: '#cookies-banner',
        data() {
            return {
                timeoutSec: 15,
            };
        },
    });

    app.component('advert-search', advertSearch);

    app.component('regional-settings-link', {
        template: '#regional-settings-link',
        methods: {
            openRegionalSettings() {
                globalActions.openRegionalSettings();
            },
        },
    });

    app.component('select-region-link', {
        template: '#select-region-link',
        methods: {
            openRegionSelectModal() {
                globalActions.openRegionSelectModal();
            },
        },
    });

    app.component('small-photo', {
        template: '#small-photo',
        props: {
            hasPhoto: {default: false},
            photoLink: {default: ''},
            hasStatus: {default: false},
            alt: {type: String, required: true},
        },
    });

    app.component('user-avatar', {
        template: '#user-avatar',
        props: {
            hasPhoto: {default: false},
            photoLink: {default: ''},
            letter: {default: ''},
            letterColor: {required: false},
            hasStatus: {default: false},
        },
    });

    app.component('field-validation', {
        template: '#field-validation',
        computed: {
            fieldError() {
                return Array.isArray(this.errors) ? this.errors[0] : this.errors;
            },
        },
        props: {
            fieldName: {
                type: String,
                required: true,
            },
            errors: {},
        },
    });

    app.component('labels-list', {
        render() {
            return null;
        },
        props: {
            labels: {
                type: Object,
                required: true,
            },
        },
        methods: {
            getLabel(key) {
                return this.labels[key];
            },
        },
    });

    app.component('datepicker', datepicker);

    app.component('timepicker', timepicker);

    app.component('logo-link', {
        template: '#logo-link',
        data() {
            return {
                isShown: true,
            };
        },
        methods: {
            _subscribeStore() {
                store.subscribe((mutation) => {
                    if (mutation.type === 'changeLogoVisibility') {
                        this.isShown = mutation.payload;
                    }
                });
            },
        },
        mounted() {
            this._subscribeStore();
        },
    });

    app.component('create-advert-buttons', {
        template: '#create-advert-buttons',
        props: {
            searchModeOptions: {},
            urls: {},
            propertyCreateActionButtonId: {
                type: String,
                required: false,
            },
            inquiryCreateActionButtonId: {
                type: String,
                required: false,
            },
        },
        components: {
            Multiselect: VueMultiselect,
        },
        data() {
            return {
                searchMode: this.searchModeOptions[0],
            };
        },
    });

    app.component('order-plan', orderPlan);

    app.component('accordion', {
        template: '#accordion',
        props: {
            header: {
                type: String,
                required: true,
            },
            isLinkStyle: {
                type: Boolean,
                required: false,
            },
        },
        computed: {},
        data() {
            return {
                isOpen: false,
            };
        },
        methods: {
            togglePanel() {
                this.isOpen = !this.isOpen;
            },
        },
    });

    app.component('confirmation-code', {
        template: '#confirmation-code',
        props: {
            fields: {
                type: Number,
                default: 4,
            },
            placeholder: {
                type: String,
                default: '_',
                required: false,
            },
            required: {
                type: Boolean,
                default: true,
            },
            checkCodeUrl: {
                type: String,
                required: true,
            },
            email: {
                type: String,
                required: true,
            },
            title: String,
            initialAutofocusIndex: {
                type: Number,
                default: -1,
            },
        },
        emits: [emitEvents.confirmationCodeComplete],
        data() {
            return {
                showValidationError: false,
                amountFields: this.initFieldsAmount(),
                initialValues: this.initValues(),
                actualValues: this.initValues(),
                autoFocusIndex: this.initialAutofocusIndex,
            };
        },
        methods: {
            initValues() {
                let values = '';
                for (let i = 0; i < this.fields; i++ ) {
                    values += this.placeholder;
                }

                return values;
            },
            initFieldsAmount() {
                const arr = [];
                for (let i = 0; i < this.fields; i++ ) {
                    arr.push(i);
                }
                return arr;
            },
            getClassObject(fieldIndex) {
                return {
                    'confirmation-code__item_invalid': this.showValidationError,
                    'confirmation-code__item_focused': this.isFocusItem(fieldIndex),
                };
            },
            cursor() {
                return this.placeholder;
            },
            setAutoFocus(event) {
                if (/[0-9]/.test(this.actualValues)) {
                    this.$refs.hiddenInput.focus();
                } else {
                    this.$refs.hiddenInput.select();
                }

                this.autoFocusIndex = this.getAutoFocusIndex();
            },
            getAutoFocusIndex() {
                let index;

                if (this.isEmptyCode()) {
                    index = 0;
                } else if (this.isFullFilledCode()) {
                    index = this.fields - 1;
                } else if (this.isPartiallyFilledCode()) {
                    index = this.actualValues.length;
                } else {
                    index = this.autoFocusIndex === this.initialAutofocusIndex ? 0 : this.autoFocusIndex;
                }

                return index;
            },

            isEmptyCode() {
                return this.actualValues === this.initValues();
            },
            isFullFilledCode() {
                return this.actualValues.length === this.fields;
            },
            isPartiallyFilledCode() {
                return this.actualValues.length >= 1 && this.actualValues.length < this.fields;
            },
            isFocusItem(index) {
                return this.autoFocusIndex < this.fields && this.autoFocusIndex === index;
            },
            async onChange(event) {
                if (this.actualValues.length === this.fields && _u.containsOnlyDigits(this.actualValues)) {
                    await this.tryAuthorizeByCode();
                } else {
                    this.showValidationError = true;
                }

                this.autoFocusIndex = this.autoFocusIndex < this.fields ?
                    this.actualValues.length :
                    this.autoFocusIndex - 1;

                this.checkValidationMessage();
            },

            async tryAuthorizeByCode() {
                const code = Number(this.actualValues);
                const url = _u.addQueryParamForUrl(this.checkCodeUrl, 'code', encodeURIComponent(code));
                try {
                    const response = await axios.post(url);
                    if (response.status === 200 && response.data === true) {
                        this.$emit(emitEvents.confirmationCodeComplete);
                    } else {
                        this.showValidationError = true;
                    }
                } catch (error) {
                    globalActions.handleAjaxErrors(error);
                }
            },

            checkValidationMessage() {
                if (this.actualValues.length < this.fields) {
                    this.showValidationError = false;
                }
            },

            resetFocus() {
                this.autoFocusIndex = this.initialAutofocusIndex;
            },
        },
    });
}


export default initCustomControls;
